import { useCallback, useEffect, useRef, useState } from 'react'
import { FixedSizeList, ListChildComponentProps } from 'react-window'

import { PageThumbnailItem } from './PageThumbnailItem'
import { ResizeHandle, SidebarInner } from './pageThumbnailSidebarStyles'

const ITEM_HEIGHT = 160
const DEFAULT_WIDTH_PX = 112
const MIN_WIDTH_PX = 56
const MAX_WIDTH_PX = 224

interface PageThumbnailSidebarProps {
  documentId: string
  totalPages: number
  currentPage: number
  isOpen: boolean
  sidebarWidth: number | null
  pdfUrl?: string
  onPageSelect: (pageNumber: number) => void
  onWidthChange: (width: number) => void
  onResizeEnd: () => void
  handleResetZoom: () => void
}

interface RowData {
  documentId: string
  currentPage: number
  totalPages: number
  pdfUrl: string | undefined
  onPageSelect: (pageNumber: number) => void
}

function ThumbnailRow({
  index,
  style,
  data,
}: ListChildComponentProps<RowData>) {
  const pageNumber = index + 1
  const { documentId, currentPage, totalPages, pdfUrl, onPageSelect } = data

  return (
    <div style={{ ...style, overflow: 'hidden' }}>
      <PageThumbnailItem
        documentId={documentId}
        pageNumber={pageNumber}
        isActive={pageNumber === currentPage}
        onClick={onPageSelect}
        pdfUrl={pdfUrl}
        totalPages={totalPages}
      />
    </div>
  )
}

export function PageThumbnailSidebar({
  documentId,
  totalPages,
  currentPage,
  isOpen,
  sidebarWidth,
  pdfUrl,
  onPageSelect,
  onWidthChange,
  onResizeEnd,
  handleResetZoom,
}: PageThumbnailSidebarProps) {
  const listRef = useRef<FixedSizeList<RowData>>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerHeight, setContainerHeight] = useState(0)
  const prevPageRef = useRef(currentPage)
  const isResizingRef = useRef(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(0)

  const previousOpenState = useRef(isOpen)
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Reset zoom after sidebar open/close transition so the viewer reflows correctly
  useEffect(() => {
    if (previousOpenState.current === isOpen) {
      return
    }

    previousOpenState.current = isOpen
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current)
    }

    resetTimeoutRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          handleResetZoom()
        })
      })
    }, 350)

    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current)
      }
    }
  }, [isOpen, handleResetZoom])

  // Track container height so FixedSizeList gets an accurate pixel value
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        setContainerHeight(entry.contentRect.height)
      }
    })

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Scroll the thumbnail list to keep the active page visible
  useEffect(() => {
    if (currentPage !== prevPageRef.current) {
      prevPageRef.current = currentPage
      listRef.current?.scrollToItem(currentPage - 1, 'smart')
    }
  }, [currentPage])

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizingRef.current) return
      const delta = e.clientX - startXRef.current
      const clamped = Math.min(
        MAX_WIDTH_PX,
        Math.max(MIN_WIDTH_PX, startWidthRef.current + delta)
      )
      onWidthChange(clamped)
    },
    [onWidthChange]
  )

  const handleResizeEnd = useCallback(() => {
    if (!isResizingRef.current) return
    isResizingRef.current = false
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    onResizeEnd()
  }, [onResizeEnd])

  useEffect(() => {
    document.addEventListener('mousemove', handleResizeMove)
    document.addEventListener('mouseup', handleResizeEnd)
    return () => {
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)
    }
  }, [handleResizeMove, handleResizeEnd])

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      isResizingRef.current = true
      startXRef.current = e.clientX
      startWidthRef.current = sidebarWidth ?? DEFAULT_WIDTH_PX
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [sidebarWidth]
  )

  const handlePageSelect = useCallback(
    (pageNumber: number) => {
      onPageSelect(pageNumber)
    },
    [onPageSelect]
  )

  // Scale row height proportionally so thumbnails (aspect-ratio driven) fit without gaps or clipping
  const effectiveWidth = sidebarWidth ?? DEFAULT_WIDTH_PX
  const itemHeight = Math.round(
    ITEM_HEIGHT * (effectiveWidth / DEFAULT_WIDTH_PX)
  )

  const itemData: RowData = {
    documentId,
    currentPage,
    totalPages,
    pdfUrl,
    onPageSelect: handlePageSelect,
  }

  return (
    <SidebarInner ref={containerRef}>
      {containerHeight > 0 && (
        <FixedSizeList<RowData>
          ref={listRef}
          height={containerHeight}
          width='100%'
          itemCount={totalPages}
          itemSize={itemHeight}
          itemData={itemData}
          overscanCount={3}>
          {ThumbnailRow}
        </FixedSizeList>
      )}
      {isOpen && <ResizeHandle onMouseDown={handleResizeStart} />}
    </SidebarInner>
  )
}
